// src/utils/invoicePdf.js
//
// Builds a clean, properly-aligned order PDF: shop header, customer
// details, an itemized table (qty / unit price / amount right-aligned),
// and totals. Used by CheckoutPage for the WhatsApp share, the "email
// the admin" draft, and the plain download button.

import { jsPDF } from 'jspdf';
import { autoTable } from 'jspdf-autotable';
import { SHOP_NAME, SHOP_TAGLINE, SHOP_ADDRESS_LINES, SHOP_PHONES, ADMIN_EMAIL } from '../config/tenant';

const inr = (n) => `Rs. ${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
// charges like packing can have paise (3% of 1,005 = 30.15) — show them exactly
const inr2 = (n) => {
  const v = Number(n || 0);
  const whole = Math.abs(v - Math.round(v)) < 0.005;
  return `Rs. ${v.toLocaleString('en-IN', { minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: whole ? 0 : 2 })}`;
};

/**
 * @param {object} order
 * @param {string} order.orderId
 * @param {{name:string, phone:string, address:string, notes?:string}} order.customer
 * @param {{name:string, qty:number, unit?:string, listPrice?:number, unitPrice:number}[]} order.items
 *        listPrice = sales price before discount, unitPrice = final price per unit
 * @param {number} order.subtotal
 * @param {{label:string, amount:number}=} order.coupon
 * @param {{label:string, pct:number, amount:number}=} order.packing   packing / delivery charge
 * @param {number} order.total
 * @returns {jsPDF}
 */
export function buildInvoicePdf({ orderId, customer, items, subtotal, coupon, packing, total }) {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const pageW  = doc.internal.pageSize.getWidth();
  const margin = 40;
  const rightX = pageW - margin;

  // ── Header ──
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.setTextColor(217, 4, 41);
  doc.text(SHOP_NAME, margin, 48);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(100, 100, 100);
  doc.text('Sivakasi Direct — Licensed Fireworks', margin, 64);
  // Address uses the dash-free form of each line: jsPDF's built-in
  // Helvetica can't render the en dash (–) and would print garbage.
  const addrLines = SHOP_ADDRESS_LINES.map(l => l.replace(/\u2013/g, '-'));
  doc.text(addrLines, margin, 78);
  let leftY = 78 + addrLines.length * 11 + 4;
  doc.text(`Phone: ${SHOP_PHONES.join('  ·  ')}`, margin, leftY);
  if (ADMIN_EMAIL) {
    leftY += 13;
    doc.text(`Email: ${ADMIN_EMAIL}`, margin, leftY);
  }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.setTextColor(20, 20, 20);
  doc.text('ORDER SUMMARY', rightX, 48, { align: 'right' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(100, 100, 100);
  doc.text(`Order ID: ${orderId || 'Pending confirmation'}`, rightX, 64, { align: 'right' });
  doc.text(`Date: ${new Date().toLocaleString('en-IN')}`, rightX, 76, { align: 'right' });

  const headerBottom = Math.max(leftY + 20, 100);
  doc.setDrawColor(230, 230, 230);
  doc.line(margin, headerBottom, rightX, headerBottom);

  // ── Bill To ──
  let y = headerBottom + 22;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(20, 20, 20);
  doc.text('BILL TO', margin, y);
  y += 15;
  doc.setFont('helvetica', 'normal');
  doc.text(customer.name, margin, y);            y += 13;
  doc.text(customer.phone, margin, y);            y += 13;
  const custAddrLines = doc.splitTextToSize(customer.address, pageW - margin * 2 - 20);
  doc.text(custAddrLines, margin, y);
  y += custAddrLines.length * 12 + 4;
  if (customer.notes) {
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(120, 120, 120);
    const noteLines = doc.splitTextToSize(`Note: ${customer.notes}`, pageW - margin * 2 - 20);
    doc.text(noteLines, margin, y);
    y += noteLines.length * 12 + 4;
    doc.setTextColor(20, 20, 20);
  }

  // ── Items table ─────────────────────────────────────────────
  // Sales Price (before discount) | Disc % | Final Price (per unit) | Qty | Amount
  const isWhole = (v) => Math.abs(v - Math.round(v)) < 0.005;
  const list  = (it) => Number(it.listPrice ?? it.unitPrice) || 0;
  const final = (it) => Number(it.unitPrice) || 0;
  // one consistent number format for the whole table (paise if any price has them)
  const dec = items.every(it => isWhole(list(it)) && isWhole(final(it)) && isWhole(final(it) * it.qty)) ? 0 : 2;
  const amt = (n) => `Rs. ${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: dec, maximumFractionDigits: dec })}`;
  const pctOf = (it) => {
    const l = list(it), f = final(it);
    if (l <= 0 || f >= l) return 0;
    const p = ((l - f) / l) * 100;
    return Math.round(p * 100) / 100;            // e.g. 90 or 33.33
  };
  const pctTxt = (p) => (p > 0 ? `${isWhole(p) ? Math.round(p) : p.toFixed(2)}%` : '-');

  autoTable(doc, {
    startY: y + 12,
    margin: { left: margin, right: margin },
    head: [['#', 'Product', 'Sales Price', 'Disc %', 'Final Price', 'Qty', 'Amount']],
    body: items.map((it, i) => [
      String(i + 1),
      it.unit ? `${it.name}  (${it.unit})` : it.name,
      amt(list(it)),
      pctTxt(pctOf(it)),
      amt(final(it)),
      String(it.qty),
      amt(final(it) * it.qty),
    ]),
    theme: 'grid',
    styles: { fontSize: 8.8, cellPadding: { top: 6, bottom: 6, left: 5, right: 5 }, textColor: [30, 30, 30],
      lineColor: [232, 222, 218], lineWidth: 0.5, valign: 'middle' },
    headStyles: { fillColor: [217, 4, 41], textColor: 255, fontStyle: 'bold', fontSize: 8.5 },
    columnStyles: {
      0: { halign: 'center', cellWidth: 24, textColor: [120, 110, 115] },
      1: { cellWidth: 'auto', fontStyle: 'bold' },
      2: { halign: 'right',  cellWidth: 72, textColor: [120, 110, 115] },
      3: { halign: 'center', cellWidth: 46, textColor: [193, 18, 31], fontStyle: 'bold' },
      4: { halign: 'right',  cellWidth: 72, textColor: [11, 122, 69], fontStyle: 'bold' },
      5: { halign: 'center', cellWidth: 34 },
      6: { halign: 'right',  cellWidth: 80, fontStyle: 'bold' },
    },
    alternateRowStyles: { fillColor: [250, 246, 244] },
    // header cells follow their column's alignment
    didParseCell: (data) => {
      if (data.section === 'head') {
        const col = data.column.index;
        if (col === 0 || col === 3 || col === 5) data.cell.styles.halign = 'center';
        if (col === 2 || col === 4 || col === 6) data.cell.styles.halign = 'right';
      }
    },
    // strike through the sales price when a discount applies
    didDrawCell: (data) => {
      if (data.section !== 'body' || data.column.index !== 2) return;
      const it = items[data.row.index];
      if (!it || pctOf(it) <= 0) return;
      const txt = Array.isArray(data.cell.text) ? data.cell.text.join(' ') : String(data.cell.text);
      doc.setFontSize(data.cell.styles.fontSize);
      const tw = doc.getTextWidth(txt);
      const rx = data.cell.x + data.cell.width - data.cell.padding('right');
      const cy = data.cell.y + data.cell.height / 2;
      doc.setDrawColor(193, 18, 31); doc.setLineWidth(0.7);
      doc.line(rx - tw - 1, cy, rx + 1, cy);
    },
  });

  // ── Totals ──────────────────────────────────────────────────
  const grossList  = items.reduce((s, it) => s + list(it) * it.qty, 0);
  const afterDisc  = items.reduce((s, it) => s + final(it) * it.qty, 0);
  const discount   = Math.max(0, grossList - afterDisc);
  const couponAmt  = coupon?.amount > 0 ? Number(coupon.amount) : 0;
  const packingAmt = packing?.amount > 0 ? Number(packing.amount) : 0;
  const exact      = afterDisc - couponAmt + packingAmt;
  const roundOff   = Math.round((Number(total) - exact) * 100) / 100;
  const overallPct = grossList > 0 ? (discount / grossList) * 100 : 0;
  const money = (n) => `Rs. ${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  let ty = doc.lastAutoTable.finalY + 20;
  const boxW = 250;
  const labelX = rightX - boxW + 12;
  const valueX = rightX - 12;
  const rows = [];
  if (discount > 0.004) {
    rows.push(['Total Sales Price', money(grossList)]);
    rows.push([`Discount (${isWhole(overallPct) ? Math.round(overallPct) : overallPct.toFixed(2)}%)`, `- ${money(discount)}`, [15, 157, 88]]);
  }
  rows.push(['Subtotal', money(afterDisc), null, true]);
  if (couponAmt > 0)  rows.push([`Coupon (${coupon.label || 'applied'})`, `- ${money(couponAmt)}`, [15, 157, 88]]);
  if (packingAmt > 0) rows.push([`${packing.label} (${Number(packing.pct) || 0}%)`, `+ ${money(packingAmt)}`]);
  if (Math.abs(roundOff) >= 0.01) rows.push(['Round off', `${roundOff > 0 ? '+' : '-'} ${money(Math.abs(roundOff))}`]);

  const boxH = rows.length * 16 + 46;
  doc.setDrawColor(232, 222, 218); doc.setFillColor(253, 250, 248);
  doc.roundedRect(rightX - boxW, ty - 14, boxW, boxH, 4, 4, 'FD');
  rows.forEach(([label, value, color, bold]) => {
    doc.setFont('helvetica', bold ? 'bold' : 'normal'); doc.setFontSize(9.5);
    doc.setTextColor(...(color || [70, 70, 70]));
    doc.text(label, labelX, ty);
    doc.text(value, valueX, ty, { align: 'right' });
    ty += 16;
  });
  doc.setDrawColor(220, 210, 205);
  doc.line(labelX, ty - 6, valueX, ty - 6);
  ty += 10;
  doc.setFont('helvetica', 'bold'); doc.setFontSize(12.5); doc.setTextColor(217, 4, 41);
  doc.text('To Pay', labelX, ty);
  doc.text(money(total), valueX, ty, { align: 'right' });

  // You saved
  const saved = discount + couponAmt;
  if (saved > 0.004) {
    const sy = doc.lastAutoTable.finalY + 6;
    doc.setFillColor(255, 243, 205); doc.setDrawColor(240, 191, 46);
    doc.roundedRect(margin, sy, 200, 40, 5, 5, 'FD');
    doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.setTextColor(122, 83, 0);
    doc.text('YOU SAVED', margin + 12, sy + 16);
    doc.setFontSize(14); doc.setTextColor(11, 122, 69);
    doc.text(money(saved), margin + 12, sy + 33);
  }
  ty += 10;
  ty += 24;
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(8.5);
  doc.setTextColor(130, 130, 130);
  doc.text('Delivery charge depends on location & parcel weight — confirmed by our team on call.', margin, ty);
  ty += 12;
  doc.text('This is a computer-generated order summary, not a tax invoice.', margin, ty);

  return doc;
}


/**
 * Sales / POS invoice. The shop identity in the header (name, address,
 * phones, email) comes straight from src/config/tenant.js — the master
 * setting — so changing SHOP_NAME there changes every printed invoice.
 * @param {object} sale  a sale record from salesAPI.getById (invoice_no,
 *   customer_name, customer_phone, sale_date, status, items[], subtotal,
 *   discount_amt, tax_amt, total_amt, payments[])
 * @returns {jsPDF}
 */
export function buildSalesInvoicePdf(sale = {}) {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const pageW  = doc.internal.pageSize.getWidth();
  const margin = 40;
  const rightX = pageW - margin;
  const num = (n) => Number(parseFloat(n) || 0);

  // ── Header — identity from config/tenant.js (master setting) ──
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.setTextColor(217, 4, 41);
  doc.text(SHOP_NAME, margin, 48);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(100, 100, 100);
  doc.text(`${SHOP_TAGLINE} - Licensed Fireworks`, margin, 64);
  const addrLines = SHOP_ADDRESS_LINES.map(l => l.replace(/\u2013/g, '-'));
  doc.text(addrLines, margin, 78);
  let leftY = 78 + addrLines.length * 11 + 4;
  doc.text(`Phone: ${SHOP_PHONES.join('  -  ')}`, margin, leftY);
  if (ADMIN_EMAIL) { leftY += 13; doc.text(`Email: ${ADMIN_EMAIL}`, margin, leftY); }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.setTextColor(20, 20, 20);
  doc.text('TAX INVOICE', rightX, 48, { align: 'right' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(100, 100, 100);
  doc.text(`Invoice: ${sale.invoice_no || '-'}`, rightX, 64, { align: 'right' });
  const dt = sale.sale_date ? new Date(sale.sale_date).toLocaleString('en-IN') : new Date().toLocaleString('en-IN');
  doc.text(`Date: ${dt}`, rightX, 76, { align: 'right' });
  if (sale.status) doc.text(`Status: ${String(sale.status).toUpperCase()}`, rightX, 88, { align: 'right' });

  const headerBottom = Math.max(leftY + 20, 104);
  doc.setDrawColor(230, 230, 230);
  doc.line(margin, headerBottom, rightX, headerBottom);

  // ── Bill To ──
  let y = headerBottom + 22;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(20, 20, 20);
  doc.text('BILL TO', margin, y);
  y += 15;
  doc.setFont('helvetica', 'normal');
  doc.text(sale.customer_name || 'Walk-in', margin, y); y += 13;
  if (sale.customer_phone) { doc.text(String(sale.customer_phone), margin, y); y += 13; }

  // ── Items ──
  const items = sale.items || [];
  autoTable(doc, {
    startY: y + 12,
    margin: { left: margin, right: margin },
    head: [['#', 'Item', 'Qty', 'Rate', 'Tax', 'Amount']],
    body: items.map((it, i) => [
      String(i + 1),
      it.product_name || it.name || '',
      String(num(it.qty)),
      inr(it.unit_price),
      inr(it.tax_amt),
      inr(it.total_amt != null ? it.total_amt : num(it.unit_price) * num(it.qty)),
    ]),
    styles: { fontSize: 9, cellPadding: 6, textColor: [30, 30, 30] },
    headStyles: { fillColor: [217, 4, 41], textColor: 255, fontStyle: 'bold' },
    columnStyles: {
      0: { halign: 'center', cellWidth: 26 },
      2: { halign: 'center', cellWidth: 42 },
      3: { halign: 'right',  cellWidth: 78 },
      4: { halign: 'right',  cellWidth: 64 },
      5: { halign: 'right',  cellWidth: 82 },
    },
    alternateRowStyles: { fillColor: [250, 246, 244] },
    didParseCell: (data) => {
      if (data.section === 'head') {
        const c = data.column.index;
        if (c === 0 || c === 2) data.cell.styles.halign = 'center';
        if (c >= 3) data.cell.styles.halign = 'right';
      }
    },
  });

  let ty = doc.lastAutoTable.finalY + 22;
  const labelX = rightX - 170;
  const row = (label, value, bold = false, color = [80, 80, 80]) => {
    doc.setFont('helvetica', bold ? 'bold' : 'normal');
    doc.setFontSize(bold ? 12 : 10);
    doc.setTextColor(...color);
    doc.text(label, labelX, ty);
    doc.text(value, rightX, ty, { align: 'right' });
    ty += bold ? 18 : 15;
  };

  row('Subtotal', inr(sale.subtotal));
  if (num(sale.discount_amt) > 0) row('Discount', `- ${inr(sale.discount_amt)}`, false, [15, 157, 88]);
  if (num(sale.tax_amt) > 0)      row('GST', inr(sale.tax_amt));
  // Packing charge as recorded on this sale (sales.packing_charge_pct / _amt, migration 012)
  if (num(sale.packing_charge_amt) > 0) {
    const pct = num(sale.packing_charge_pct);
    row(`${sale.packing_charge_label || 'Packing Charges'}${pct > 0 ? ` (${pct}%)` : ''}`, `+ ${inr2(sale.packing_charge_amt)}`);
  }
  doc.setDrawColor(220, 220, 220);
  doc.line(labelX, ty, rightX, ty);
  ty += 14;
  row('Total', inr(sale.total_amt), true, [217, 4, 41]);

  // ── Payments ──
  const pays = sale.payments || [];
  if (pays.length) {
    ty += 12;
    doc.setFont('helvetica', 'bold'); doc.setFontSize(10); doc.setTextColor(20, 20, 20);
    doc.text('Payments', labelX, ty); ty += 15;
    doc.setFont('helvetica', 'normal'); doc.setFontSize(10); doc.setTextColor(80, 80, 80);
    let paid = 0;
    pays.forEach(p => {
      paid += num(p.amount);
      doc.text(p.method_name || 'Cash', labelX, ty);
      doc.text(inr(p.amount), rightX, ty, { align: 'right' });
      ty += 14;
    });
    const bal = num(sale.total_amt) - paid;
    if (bal > 0) {
      doc.setFont('helvetica', 'bold'); doc.setTextColor(217, 4, 41);
      doc.text('Balance', labelX, ty);
      doc.text(inr(bal), rightX, ty, { align: 'right' });
      ty += 16;
    }
  }

  ty += 20;
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(8.5);
  doc.setTextColor(130, 130, 130);
  doc.text('Thank you for your purchase. Goods once sold are subject to shop policy.', margin, ty);

  return doc;
}

/** Wraps a jsPDF document as a File, for downloads and Web Share. */
export function pdfToFile(doc, filename) {
  return new File([doc.output('blob')], filename, { type: 'application/pdf' });
}
